import { jsx, jsxs } from "react/jsx-runtime";
import assert from "assert";
import { forwardRef, cloneElement } from "react";
import { useFallbackId } from "../../hooks/useFallbackId.mjs";
import { iconContainerSize, iconSize } from "../../hooks/useIcon/index.mjs";
import { Bleed } from "../Bleed/Bleed.mjs";
import { Box } from "../Box/Box.mjs";
import { useButtonStyles, ButtonOverlays } from "../Button/Button.mjs";
import { Text } from "../Text/Text.mjs";
import { TooltipRenderer } from "../TooltipRenderer/TooltipRenderer.mjs";
import { buildDataAttributes } from "../private/buildDataAttributes.mjs";
import { button } from "./ButtonIcon.css.mjs";
const buttonIconVariants = ["soft", "transparent"];
const buttonIconTones = ["neutral", "formAccent"];
const buttonIconSizes = ["small", "standard", "large"];
const padding = {
  small: "xxsmall",
  standard: "xsmall",
  large: "xsmall"
};
const ButtonIconContent = forwardRef(
  ({
    icon,
    label,
    id,
    size = "standard",
    tone = "neutral",
    variant = "soft",
    type = "button",
    bleed,
    tooltipPlacement,
    onClick,
    onMouseDown,
    onKeyUp,
    onKeyDown,
    "aria-haspopup": ariaHasPopUp,
    "aria-expanded": ariaExpanded,
    "aria-describedby": ariaDescribedBy,
    tabIndex,
    data,
    ...restProps
  }, forwardedRef) => {
    const { root, content } = useButtonStyles({
      variant,
      tone,
      size: size === "small" ? "small" : "standard",
      radius: "full"
    });
    assert(
      icon && icon.props.size === void 0,
      "Icons cannot set the 'size' prop when passed to a ButtonIcon component"
    );
    const button$1 = /* @__PURE__ */ jsx(
      Box,
      {
        component: "button",
        type,
        id,
        zIndex: 0,
        ref: forwardedRef,
        "aria-label": label,
        "aria-haspopup": ariaHasPopUp,
        "aria-expanded": ariaExpanded,
        "aria-describedby": ariaDescribedBy,
        onClick,
        onKeyUp,
        onKeyDown,
        onMouseDown,
        maxWidth: "content",
        tabIndex,
        ...root,
        className: [root.className, button],
        ...buildDataAttributes({ data, validateRestProps: restProps }),
        children: /* @__PURE__ */ jsxs(Box, { ...content, padding: padding[size], children: [
          /* @__PURE__ */ jsx(
            ButtonOverlays,
            {
              variant,
              tone,
              radius: "full",
              forceActive: ariaExpanded === "true" || ariaExpanded === true
            }
          ),
          /* @__PURE__ */ jsx(
            Box,
            {
              component: "span",
              display: "block",
              position: "relative",
              className: size === "large" ? iconContainerSize() : iconSize({ size, crop: true }),
              children: cloneElement(icon, {
                tone: icon.props.tone || tone,
                size: "fill"
              })
            }
          )
        ] })
      }
    );
    const shouldBleed = typeof bleed === "undefined" && variant === "transparent" || bleed;
    return shouldBleed ? /* @__PURE__ */ jsx(Bleed, { space: padding[size], children: button$1 }) : button$1;
  }
);
const ButtonIcon = forwardRef(
  ({ id, label, tooltipPlacement, ...restProps }, forwardedRef) => {
    const resolvedId = useFallbackId(id);
    return /* @__PURE__ */ jsx(
      TooltipRenderer,
      {
        tooltip: /* @__PURE__ */ jsx(Text, { children: label }),
        placement: tooltipPlacement,
        children: ({ triggerProps: { ref: triggerRef, tabIndex } }) => /* @__PURE__ */ jsx(
          ButtonIconContent,
          {
            id: resolvedId,
            label,
            ref: (node) => {
              if (typeof forwardedRef === "function") {
                forwardedRef(node);
              } else if (forwardedRef) {
                forwardedRef.current = node;
              }
              if (typeof triggerRef === "function") {
                triggerRef(node);
              }
            },
            tabIndex,
            ...restProps
          }
        )
      }
    );
  }
);
ButtonIcon.displayName = "ButtonIcon";
export {
  ButtonIcon,
  buttonIconSizes,
  buttonIconTones,
  buttonIconVariants
};
